import type { DHWPressureData } from '../types';

const COLD_MAIN_TYPE_LABELS: Record<string, string> = {
  mdpe_25_blue: '25mm blue MDPE',
  mdpe_20: '20mm MDPE',
  copper_22: '22mm copper',
  copper_15: '15mm copper',
  lead: 'Lead',
  unknown: 'Not assessed yet',
};

interface Props {
  data: DHWPressureData;
  onClick: () => void;
}

export function DHWPressureTile({ data, onClick }: Props) {
  const v = (val: string, map: Record<string, string>) => map[val] ?? val ?? '—';
  const isNotAssessed = (val: string) => !val || val === 'unknown';

  // Determine data collection status
  const hasStaticMeasurements = data.mainsFlowLpm !== null && data.mainsStaticPressureBar !== null;
  const hasDynamicMeasurements = data.mainsDynamicFlowLpm !== null && data.mainsDynamicPressureBar !== null;
  
  const statusLabel = hasStaticMeasurements
    ? (hasDynamicMeasurements ? 'collected (full)' : 'collected (basic)')
    : 'not collected';
  
  const statusLevel = hasStaticMeasurements
    ? (hasDynamicMeasurements ? 'high' : 'medium')
    : 'low';

  return (
    <div className="hub-tile" onClick={onClick} role="button" tabIndex={0}>
      <div className="hub-tile__header">
        <h3 className="hub-tile__title">Cold Mains Performance</h3>
        <span className={`hub-tile__badge hub-tile__badge--${statusLevel}`}>
          {statusLabel}
        </span>
      </div>
      <div className="hub-tile__body">
        <div className="hub-tile__row">
          <span className="hub-tile__label">Cold main type</span>
          <span className={`hub-tile__value${isNotAssessed(data.coldMainType) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.coldMainType, COLD_MAIN_TYPE_LABELS)}
          </span>
        </div>

        <div className="hub-tile__row">
          <span className="hub-tile__label">Flow</span>
          <span className={`hub-tile__value${data.mainsFlowLpm === null ? ' hub-tile__value--unknown' : ''}`}>
            {data.mainsFlowLpm !== null ? `${data.mainsFlowLpm} L/min` : 'Not measured'}
          </span>
        </div>

        <div className="hub-tile__row">
          <span className="hub-tile__label">Static pressure</span>
          <span className={`hub-tile__value${data.mainsStaticPressureBar === null ? ' hub-tile__value--unknown' : ''}`}>
            {data.mainsStaticPressureBar !== null ? `${data.mainsStaticPressureBar} bar` : 'Not measured'}
          </span>
        </div>

        <div className="hub-tile__row">
          <span className="hub-tile__label">Dynamic result</span>
          <span className={`hub-tile__value${(data.mainsDynamicFlowLpm === null || data.mainsDynamicPressureBar === null) ? ' hub-tile__value--unknown' : ''}`}>
            {(data.mainsDynamicFlowLpm !== null && data.mainsDynamicPressureBar !== null)
              ? `${data.mainsDynamicFlowLpm} L/min @ ${data.mainsDynamicPressureBar} bar`
              : 'Optional'}
          </span>
        </div>
      </div>

      {(data.mainsFlowLpm === null || data.mainsStaticPressureBar === null) && (
        <div className="hub-tile__warnings">
          <span className="hub-tile__warning">Cold mains not assessed for accurate recommendations</span>
        </div>
      )}

      <span className="hub-tile__tap-hint">tap to edit</span>
    </div>
  );
}
